import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Button } from './ui/button';
import { FileText, CheckCircle, Calendar, Globe } from 'lucide-react';

const ViewConsentDialog = ({ 
  open, 
  onOpenChange, 
  consent,
  patientName 
}) => {
  if (!consent) return null;

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const languageLabel = consent.language === 'es' ? 'Spanish' : 'English';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="w-5 h-5 text-gray-600" />
            Signed Consent
          </DialogTitle>
        </DialogHeader>
        
        <DialogBody className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Patient & Date Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Patient</p>
              <p className="font-medium text-gray-900">{patientName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Language</p>
              <div className="flex items-center gap-1">
                <Globe className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">{languageLabel}</span>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Signed On</p>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900">{formatDate(consent.timestamp)}</span>
            </div>
          </div>

          {/* Services Acknowledged */}
          {consent.services_acknowledged?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Services Acknowledged</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Service</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-600 w-12">Qty</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600 w-20">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consent.services_acknowledged.map((s, idx) => (
                      <tr key={idx} className="border-b border-gray-100 last:border-0">
                        <td className="px-3 py-2 text-gray-900">{s.name}</td>
                        <td className="px-3 py-2 text-center text-gray-600">{s.quantity}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900">${(s.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Medications Acknowledged */}
          {consent.medications_acknowledged?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Medications Acknowledged</p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Medication</th>
                      <th className="text-center px-3 py-2 font-medium text-gray-600 w-12">Qty</th>
                      <th className="text-right px-3 py-2 font-medium text-gray-600 w-20">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consent.medications_acknowledged.map((m, idx) => (
                      <tr key={idx} className="border-b border-gray-100 last:border-0">
                        <td className="px-3 py-2 text-gray-900">{m.name}</td>
                        <td className="px-3 py-2 text-center text-gray-600">{m.quantity}</td>
                        <td className="px-3 py-2 text-right font-medium text-gray-900">${(m.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Total Acknowledged */}
          <div className="bg-gray-100 p-3 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">Total Amount Acknowledged</span>
              <span className="text-xl font-bold text-gray-900">${(consent.total_acknowledged || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Signature */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Patient Signature</p>
            <div className="border border-gray-200 rounded-lg p-3 bg-white">
              {consent.signature ? (
                <img 
                  src={consent.signature} 
                  alt="Patient Signature" 
                  className="w-full h-32 object-contain"
                  data-testid="consent-signature-image"
                />
              ) : (
                <div className="h-32 flex items-center justify-center text-gray-400">
                  No signature available
                </div>
              )}
            </div>
          </div>

          {/* Verification Badge */}
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Consent Verified</p>
              <p className="text-xs text-green-600">Patient signed and acknowledged the treatment</p>
            </div>
          </div>
        </DialogBody>

        <div className="border-t pt-4">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            className="w-full"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewConsentDialog;
